import {
  BottomSheet,
  Illustration,
  Span,
  Text,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { DialogContent, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { Li, Ul } from "@src/appV2/lib/Lists";
import { STATE_LICENSE_REQUIREMENT_ID } from "@src/constants";
import pluralize from "pluralize";
import { useCallback } from "react";

import { Button } from "../../components/Button";
import { DialogFooter } from "../../components/DialogFooter";
import { SHIFT_BLOCK_ACTIONS } from "./utils";

export interface MissingDocuments {
  reqId: string;
  name: string;
  visibleToHCP: boolean;
}

interface Props {
  analyticsProps: {
    shiftBlockId: string;
    facilityId: string;
    location: string;
  };
  missingDocuments: Set<MissingDocuments>;
  modalState: UseModalState;
  onConfirm: () => void;
}

export function MissingDocumentsBottomSheet(props: Props) {
  const { modalState, onConfirm, missingDocuments, analyticsProps } = props;

  const visibleMissingDocuments: MissingDocuments[] = [];
  const invisibleMissingDocuments: MissingDocuments[] = [];

  missingDocuments.forEach((requirement) => {
    if (requirement.reqId === STATE_LICENSE_REQUIREMENT_ID) {
      visibleMissingDocuments.push({
        ...requirement,
        name: "State License (upload as Miscellaneous)",
      });
    } else if (requirement.visibleToHCP) {
      visibleMissingDocuments.push(requirement);
    } else {
      invisibleMissingDocuments.push(requirement);
    }
  });
  const hasMoreDocuments = missingDocuments.size > 6;

  const handleClose = useCallback(() => {
    modalState.closeModal();
    logEvent(APP_V2_USER_EVENTS.SHIFT_BLOCKS, {
      action: SHIFT_BLOCK_ACTIONS.CLOSE_MISSING_DOCUMENTS_POPUP_TAPPED,
      missingDocuments,
      ...analyticsProps,
    });
  }, [analyticsProps, missingDocuments, modalState]);

  const handleUploadDocuments = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.SHIFT_BLOCKS, {
      action: SHIFT_BLOCK_ACTIONS.PROCEED_TO_MISSING_DOCUMENTS_TAPPED,
      missingDocuments,
      ...analyticsProps,
    });
    onConfirm();
  }, [analyticsProps, missingDocuments, onConfirm]);

  return (
    <BottomSheet
      canDismiss
      footer={
        <DialogFooter orientation="horizontal">
          <Button fullWidth size="large" variant="outlined" onClick={handleClose}>
            Close
          </Button>
          {visibleMissingDocuments.length > 0 && (
            <Button fullWidth size="large" variant="contained" onClick={handleUploadDocuments}>
              Upload Docs
            </Button>
          )}
        </DialogFooter>
      }
      modalState={modalState}
      onClose={handleClose}
    >
      <DialogContent>
        <Stack spacing={4} alignItems="center">
          <Text bold variant="h4" sx={{ textAlign: "center" }}>
            You&apos;re Missing{" "}
            <Span color={(theme) => theme.palette.primary.main}>{missingDocuments.size}</Span>{" "}
            {pluralize("Document", missingDocuments.size)}
          </Text>
          <Illustration type="document-upload" />

          {visibleMissingDocuments.length > 0 && (
            <Stack spacing={2} sx={{ width: "100%" }}>
              <Text align="center">
                {`To complete your booking, the following ${pluralize(
                  "document",
                  missingDocuments.size
                )} must be uploaded and approved:`}
              </Text>
              <Ul>
                {visibleMissingDocuments.slice(0, 6).map((document) => (
                  <Li key={document.reqId}>
                    <Text bold>{document.name}</Text>
                  </Li>
                ))}
              </Ul>
            </Stack>
          )}
          {invisibleMissingDocuments.length > 0 && visibleMissingDocuments.length < 6 && (
            <Stack spacing={2} sx={{ width: "100%" }}>
              <Text align="center">
                We will process the following requirements for you. Please contact
                onboarding@clipboardhealth.com if you have any questions:
              </Text>
              <Ul>
                {invisibleMissingDocuments
                  .slice(0, 6 - visibleMissingDocuments.length)
                  .map((document) => (
                    <Li key={document.reqId}>
                      <Text bold>{document.name}</Text>
                    </Li>
                  ))}
              </Ul>
            </Stack>
          )}
          {hasMoreDocuments && (
            <Text align="center">
              Click to upload your documents and view the remaining missing requirements.
            </Text>
          )}
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
